import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { mediaTagApiClient } from '@src/api/tenant/mediaTag/mediaTagApiClient';
import { supportServiceTagApiClient } from '@src/api/tenant/supportService/supportServiceTagApiClient';
import { MediaTag } from '@src/api/types/mediaTag';
import { SupportServiceTag } from '@src/api/types/supportServiceTag';
import { firstError } from '@src/utils/errorMessageUtils';
import { useState } from 'react';

type TagType = 'media' | 'supportService';

interface UseTagManagementProps {
  initialTags: number[];
  availableTags: MediaTag[] | SupportServiceTag[];
  setAvailableTags: (tags: MediaTag[] | SupportServiceTag[]) => void;
  onTagsChange: (tags: number[]) => void;
  type: TagType;
}

const apiClientMap = {
  media: mediaTagApiClient,
  supportService: supportServiceTagApiClient,
};

export const useTagManagement = ({
  initialTags,
  availableTags,
  setAvailableTags,
  onTagsChange,
  type,
}: UseTagManagementProps) => {
  const [newTagName, setNewTagName] = useState('');
  const [tagError, setTagError] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<number[]>(initialTags);

  const handleNewTag = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (newTagName.trim() === '') {
        return;
      }

      const formData = {
        name: newTagName,
        enabled: true,
      };

      const apiClient = apiClientMap[type];
      const response = await apiClient.create(formData);
      handleApiResponse(
        response,
        (data) => {
          addTag(data);
          setAvailableTags([...availableTags, data]);
          setNewTagName('');
          setTagError(null);
        },
        (errors) => {
          setTagError(firstError(errors.name));
        },
      );
    }
  };

  const addTag = (tag: MediaTag | SupportServiceTag) => {
    if (initialTags.includes(tag.id)) {
      return;
    }
    const newTags = [...initialTags, tag.id];
    setSelectedTags(newTags);
    onTagsChange(newTags);
  };

  const handleAddTag = (tagId: string) => {
    const tag = availableTags.find((t) => t.id.toString() === tagId);
    if (tag) {
      addTag(tag);
    }
  };

  const handleRemoveTag = (index: number) => {
    const newTags = initialTags.filter((_, i) => i !== index);
    setSelectedTags(newTags);
    onTagsChange(newTags);
  };

  return {
    selectedTags,
    newTagName,
    setNewTagName,
    handleNewTag,
    handleAddTag,
    handleRemoveTag,
    tagError,
  };
};
